/**
 * Product Media Carousel Web Component
 * Handles media gallery with touch/swipe support and thumbnail navigation
 */

class ProductMediaSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector(".product-media-carousel__slider");
    this.sliderItems = this.querySelectorAll(".product-media-carousel__slide");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.currentPage = 0;
    this.isScrolling = false;

    if (!this.slider || !this.sliderItems.length) return;

    // Only initialize if we're on desktop (990px+)
    // On mobile/tablet, use native smooth scrolling
    if (window.innerWidth >= 990) {
      this.initButtons();
      this.initScroll();
      this.updateButtons();
    }
    // Don't initialize touch handlers - let native scrolling handle it
  }

  initButtons() {
    if (!this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initScroll() {
    // Don't update page during scroll for free scrolling
    // Only update buttons at scroll end for navigation state
    this.slider.addEventListener("scrollend", () => {
      this.updateButtons();
    });
  }

  initTouch() {
    // Disabled - using native touch scrolling for smooth performance
    // Native scrolling is much smoother than JavaScript-based scrolling
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.name === "next" ? 1 : -1;
    this.slideTo(this.currentPage + step);
  }

  updateCurrentPage() {
    // Only update when explicitly called (e.g., from button clicks or thumbnail clicks)
    // Don't update during free scrolling
    const scrollLeft = this.slider.scrollLeft;
    const slideWidth = this.sliderItems[0].offsetWidth;
    this.currentPage = Math.round(scrollLeft / slideWidth);

    // Update active slide only when explicitly setting
    this.sliderItems.forEach((item, index) => {
      if (index === this.currentPage) {
        item.classList.add("is-active");
        item.setAttribute("aria-current", "true");
      } else {
        item.classList.remove("is-active");
        item.setAttribute("aria-current", "false");
      }
    });

    // Dispatch event for thumbnail sync
    this.dispatchEvent(
      new CustomEvent("slideChange", {
        bubbles: true,
        detail: { currentPage: this.currentPage },
      })
    );
  }

  updateButtons() {
    if (!this.prevButton || !this.nextButton) return;

    // Calculate current position for button states without forcing page updates
    const scrollLeft = this.slider.scrollLeft;
    const slideWidth = this.sliderItems[0]?.offsetWidth || 0;
    const approximatePage =
      slideWidth > 0 ? Math.round(scrollLeft / slideWidth) : 0;

    this.prevButton.disabled = approximatePage <= 0;
    this.nextButton.disabled = approximatePage >= this.sliderItems.length - 1;
  }

  slideTo(pageIndex) {
    if (pageIndex < 0 || pageIndex >= this.sliderItems.length) return;

    const slideWidth = this.sliderItems[0].offsetWidth;
    const scrollPosition = slideWidth * pageIndex;

    this.slider.scrollTo({
      left: scrollPosition,
      behavior: "auto",
    });

    this.currentPage = pageIndex;
    this.updateButtons();
  }

  setActiveMedia(mediaId) {
    const targetSlide = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!targetSlide) return;

    const slideIndex = Array.from(this.sliderItems).indexOf(targetSlide);
    if (slideIndex !== -1) {
      this.slideTo(slideIndex);
    }
  }
}

class ThumbnailCarousel extends HTMLElement {
  constructor() {
    super();
    this.list = this.querySelector(".thumbnail-carousel__list");
    this.thumbnails = this.querySelectorAll(".thumbnail-carousel__thumb");
    this.prevButton = this.querySelector(".thumbnail-carousel__button--prev");
    this.nextButton = this.querySelector(".thumbnail-carousel__button--next");
    this.currentIndex = 0;

    if (!this.list || !this.thumbnails.length) return;

    this.initThumbnails();
    this.initButtons();
    this.initScroll();
    this.listenToSlider();
  }

  initThumbnails() {
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener("click", (e) => {
        e.preventDefault();
        this.selectThumbnail(index);

        // Notify main slider
        const mediaId = thumb.dataset.target;
        const slider = document.querySelector("product-media-slider");
        if (slider) {
          slider.setActiveMedia(mediaId);
        }
      });
    });
  }

  initButtons() {
    if (!this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener("click", () => {
      this.scrollThumbnails(-1);
    });

    this.nextButton.addEventListener("click", () => {
      this.scrollThumbnails(1);
    });
  }

  initScroll() {
    this.list.addEventListener("scroll", () => {
      this.updateButtonStates();
    });
  }

  listenToSlider() {
    document.addEventListener("slideChange", (event) => {
      this.selectThumbnail(event.detail.currentPage);
    });
  }

  selectThumbnail(index) {
    if (index < 0 || index >= this.thumbnails.length) return;

    this.currentIndex = index;

    // Update active states
    this.thumbnails.forEach((thumb, i) => {
      if (i === index) {
        thumb.classList.add("is-active");
        thumb.setAttribute("aria-current", "true");
      } else {
        thumb.classList.remove("is-active");
        thumb.setAttribute("aria-current", "false");
      }
    });

    // Scroll thumbnail into view
    this.scrollThumbnailIntoView(index);
  }

  scrollThumbnailIntoView(index) {
    const thumbnail = this.thumbnails[index];
    if (!thumbnail) return;

    const listRect = this.list.getBoundingClientRect();
    const thumbRect = thumbnail.getBoundingClientRect();

    if (thumbRect.left < listRect.left) {
      this.list.scrollBy({
        left: thumbRect.left - listRect.left - 8,
        behavior: "smooth",
      });
    } else if (thumbRect.right > listRect.right) {
      this.list.scrollBy({
        left: thumbRect.right - listRect.right + 8,
        behavior: "smooth",
      });
    }
  }

  scrollThumbnails(direction) {
    const scrollAmount = this.list.offsetWidth * 0.7;
    this.list.scrollBy({
      left: scrollAmount * direction,
      behavior: "smooth",
    });
  }

  updateButtonStates() {
    if (!this.prevButton || !this.nextButton) return;

    const isAtStart = this.list.scrollLeft <= 0;
    const isAtEnd =
      this.list.scrollLeft + this.list.offsetWidth >= this.list.scrollWidth - 1;

    this.prevButton.disabled = isAtStart;
    this.nextButton.disabled = isAtEnd;
  }
}

class MediaCarousel extends HTMLElement {
  constructor() {
    super();
    this.desktopLayout = this.dataset.desktopLayout || "carousel";
    this.mobileLayout = this.dataset.mobileLayout || "carousel";
  }

  connectedCallback() {
    this.classList.add("media-carousel--loaded");
  }
}

class ProductMediaModal extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector(".product-media-modal__toggle");
    this.dialog = this.querySelector(".product-media-modal__dialog");

    if (!this.closeButton) return;

    this.closeButton.addEventListener("click", this.hide.bind(this));

    this.addEventListener("click", (event) => {
      if (event.target === this) {
        this.hide();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.hasAttribute("open")) {
        this.hide();
      }
    });
  }

  show(opener) {
    this.setAttribute("open", "");
    document.body.classList.add("overflow-hidden");

    // Get media ID from active slide (always use current visible slide)
    let mediaId = null;
    const slider = document.querySelector("product-media-slider");
    if (slider) {
      // Find the slide that's most visible in viewport
      const sliderElement = slider.querySelector(
        ".product-media-carousel__slider"
      );
      if (sliderElement) {
        const slides = slider.querySelectorAll(
          ".product-media-carousel__slide"
        );
        const sliderRect = sliderElement.getBoundingClientRect();
        const sliderCenter = sliderRect.left + sliderRect.width / 2;

        // Find the slide closest to center
        let closestSlide = null;
        let closestDistance = Infinity;

        slides.forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - sliderCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSlide = slide;
          }
        });

        if (closestSlide) {
          mediaId = closestSlide.dataset.mediaId;
        }
      }

      // Fallback to active slide if no closest found
      if (!mediaId) {
        const activeSlide = slider.querySelector(
          ".product-media-carousel__slide.is-active"
        );
        if (activeSlide) {
          mediaId = activeSlide.dataset.mediaId;
        }
      }
    }

    if (mediaId) {
      this.showMedia(mediaId);
    }
  }

  hide() {
    this.removeAttribute("open");
    document.body.classList.remove("overflow-hidden");
  }

  showMedia(mediaId) {
    const items = this.querySelectorAll(".product-media-modal__content-item");
    items.forEach((item) => {
      if (item.dataset.mediaId === mediaId) {
        item.classList.add("is-active");
      } else {
        item.classList.remove("is-active");
      }
    });
  }
}

// Register Web Components
if (!customElements.get("product-media-slider")) {
  customElements.define("product-media-slider", ProductMediaSliderComponent);
}

if (!customElements.get("thumbnail-carousel")) {
  customElements.define("thumbnail-carousel", ThumbnailCarousel);
}

if (!customElements.get("media-carousel")) {
  customElements.define("media-carousel", MediaCarousel);
}

if (!customElements.get("product-media-modal")) {
  customElements.define("product-media-modal", ProductMediaModal);
}

// Variant change listener
if (typeof window.variantChangeCallback === "undefined") {
  window.variantChangeCallback = [];
}

window.variantChangeCallback.push((variant) => {
  if (!variant || !variant.featured_media) return;

  const slider = document.querySelector("product-media-slider");
  if (slider) {
    const mediaId = `${variant.featured_media.id}`;
    const targetSlide = slider.querySelector(`[data-media-id*="${mediaId}"]`);

    if (targetSlide) {
      slider.setActiveMedia(targetSlide.dataset.mediaId);
    }
  }
});
